import type {
  PresenceCameraConfig,
  PresenceSpace,
  SpawnPoint,
  WorldDefinition,
} from '@/world-prototype/types';
import { boundsFromPolygon } from '@/world-prototype/movement/walkArea';
import {
  placeObjects,
  standWorldFromDef,
  type WorldObjectDef,
} from '@/world-prototype/objects/placeObject';
import { placeGroundDecals } from '@/world-prototype/objects/groundDecal';
import { placeNatureScatters } from '@/world-prototype/objects/natureScatter';
import { createYardScenery } from '@/world-prototype/scenery/createYardScenery';
import {
  backyardAspectFor,
  backyardMasterFor,
  raleighAspectFor,
  raleighMasterFor,
  spanishForkAspectFor,
  spanishForkMasterFor,
  withResolvedLayout,
  type BackyardPresentationMode,
} from '@/world-prototype/camera/backyardPresentation';
import { BACKYARD_PROP_DEFS } from '@/world-prototype/worlds/jacksonvilleBackyardProps';
import { BACKYARD_GROUND_DECAL_DEFS } from '@/world-prototype/worlds/jacksonvilleBackyardGroundDecals';
import { BACKYARD_NATURE_SCATTER_DEFS } from '@/world-prototype/worlds/jacksonvilleBackyardNatureScatter';
import { RALEIGH_PROP_DEFS } from '@/world-prototype/worlds/raleighBackyardProps';
import { RALEIGH_GROUND_DECAL_DEFS } from '@/world-prototype/worlds/raleighBackyardGroundDecals';
import { SPANISH_FORK_PROP_DEFS } from '@/world-prototype/worlds/spanishForkBackyardProps';
import { SPANISH_FORK_GROUND_DECAL_DEFS } from '@/world-prototype/worlds/spanishForkBackyardGroundDecals';
import {
  applyObjectLayoutOverride,
} from '@/world-prototype/ownerEdit/layoutOverrideTypes';
import { getLayoutOverrides } from '@/world-prototype/ownerEdit/layoutOverrides';
import {
  cameraDepthFromYardScale,
  HUBERT_SCALE,
  RALEIGH_SCALE,
  SPANISH_FORK_SCALE,
} from '@/world-prototype/scale/worldScale';

/** @deprecated Prefer BACKYARD_MASTER_DESKTOP_ASPECT — kept for tests. */
export const BACKYARD_MASTER_ASPECT = backyardAspectFor('desktop');

/**
 * Fence inset ≈ 0.015 normalized (~25px at 1672-wide plate).
 * Small enough that rear corners stay reachable; large enough that feet
 * clear fence boards.
 */
export const BACKYARD_WALK_FENCE_INSET = 0.015;

/**
 * Full interior walkable ground: grass + patio.
 * Clockwise from rear-left along the fence, then through the foreground.
 * Vertices sit ~BACKYARD_WALK_FENCE_INSET inside the visible fence base.
 */
export const BACKYARD_WALK_POLYGON: readonly SpawnPoint[] = [
  { x: 0.41, y: 0.35 },
  { x: 0.73, y: 0.35 },
  { x: 0.855, y: 0.51 },
  { x: 0.94, y: 0.6 },
  { x: 0.98, y: 0.66 },
  { x: 0.985, y: 0.985 },
  { x: 0.02, y: 0.985 },
  { x: 0.02, y: 0.73 },
  { x: 0.045, y: 0.67 },
  { x: 0.2, y: 0.515 },
];

/** Debug-only reach checks for the yard extremities. */
export const BACKYARD_WALK_REACH_MARKERS: readonly {
  id: string;
  label: string;
  x: number;
  y: number;
}[] = [
  { id: 'rear-left', label: 'rear L', x: 0.42, y: 0.36 },
  { id: 'rear-right', label: 'rear R', x: 0.72, y: 0.36 },
  { id: 'behind-shed', label: 'shed rear', x: 0.75, y: 0.41 },
  { id: 'patio-left', label: 'patio L', x: 0.1, y: 0.93 },
  { id: 'patio-right', label: 'patio R', x: 0.9, y: 0.93 },
];

/** Storage Shed prop — authored in the Hubert backyard prop pack (World Editor). */
export const BACKYARD_SHED: WorldObjectDef =
  BACKYARD_PROP_DEFS.find((def) => def.id === 'shed')!;

const BACK_TO_MAP = {
  id: 'to-places-map',
  label: 'Back to Map',
  interactionType: 'travel' as const,
  targetLocationId: 'places-map',
};

/**
 * Storybook camera — slightly elevated, slightly behind, looking gently
 * into the yard. Responsive zoom (desktop fit / phone crop) is separate.
 */
function storybookCamera(
  partial?: Partial<PresenceCameraConfig>,
): PresenceCameraConfig {
  return {
    followSmoothing: 0.42,
    edgeSmoothing: 0.55,
    framingBiasX: 0.03,
    framingBiasY: 0.05,
    lookAhead: 0.03,
    panBounds: {
      xMin: 0.38,
      xMax: 0.62,
      yMin: 0.42,
      yMax: 0.72,
    },
    depthScaleNear: 1.08,
    depthScaleFar: 0.82,
    depthYNear: 0.86,
    depthYFar: 0.32,
    ...partial,
  };
}

function placeBackyardPack(mode: BackyardPresentationMode) {
  const worldAspect = backyardAspectFor(mode);
  const scaleCtx = { sceneId: 'hubert', worldAspect };
  const overrides = getLayoutOverrides();
  const props = placeObjects(
    BACKYARD_PROP_DEFS.map((d) =>
      withResolvedLayout(
        applyObjectLayoutOverride(d, 'hubert', mode, overrides),
        mode,
        scaleCtx,
      ),
    ),
    { worldAspect },
  );
  const decals = placeGroundDecals(
    BACKYARD_GROUND_DECAL_DEFS.map((d) =>
      withResolvedLayout(
        applyObjectLayoutOverride(d, 'hubert', mode, overrides),
        mode,
      ),
    ),
    { worldAspect },
  );
  const scatter = placeNatureScatters(
    BACKYARD_NATURE_SCATTER_DEFS.map((d) =>
      withResolvedLayout(
        applyObjectLayoutOverride(d, 'hubert', mode, overrides),
        mode,
        scaleCtx,
      ),
    ),
    { worldAspect },
  );
  return { props, decals, scatter, worldAspect };
}

/**
 * Rebuild Hubert backyard master art + placed scenery/objects for a
 * presentation mode. Mutates the shared presence space in place.
 */
export function applyBackyardPresentation(
  space: PresenceSpace,
  mode: BackyardPresentationMode,
): void {
  if (space.id !== 'hubert') return;
  const master = backyardMasterFor(mode);
  const { props, decals, scatter, worldAspect } = placeBackyardPack(mode);
  space.worldAspectRatio = worldAspect;
  space.scenery = createYardScenery({
    masterArtwork: {
      src: master.src,
      alt: master.alt,
    },
    extras: [
      ...decals.sceneryLayers,
      ...scatter.sceneryLayers,
      ...props.sceneryLayers,
    ],
  });
  space.walkObstacles = [
    ...decals.walkObstacles,
    ...props.walkObstacles,
  ];
  space.interactiveObjects = [
    ...scatter.interactiveObjects,
    ...props.interactiveObjects,
  ];
}

function placeRaleighPack(mode: BackyardPresentationMode) {
  const worldAspect = raleighAspectFor(mode);
  const scaleCtx = { sceneId: 'raleigh', worldAspect };
  const overrides = getLayoutOverrides();
  const props = placeObjects(
    RALEIGH_PROP_DEFS.map((d) =>
      withResolvedLayout(
        applyObjectLayoutOverride(d, 'raleigh', mode, overrides),
        mode,
        scaleCtx,
      ),
    ),
    { worldAspect },
  );
  const decals = placeGroundDecals(
    RALEIGH_GROUND_DECAL_DEFS.map((d) =>
      withResolvedLayout(
        applyObjectLayoutOverride(d, 'raleigh', mode, overrides),
        mode,
      ),
    ),
    { worldAspect },
  );
  return { props, decals, worldAspect };
}

/**
 * Rebuild Holly Springs (raleigh) backyard master art + placed scenery/objects.
 */
export function applyRaleighPresentation(
  space: PresenceSpace,
  mode: BackyardPresentationMode,
): void {
  if (space.id !== 'raleigh') return;
  const master = raleighMasterFor(mode);
  const { props, decals, worldAspect } = placeRaleighPack(mode);
  space.worldAspectRatio = worldAspect;
  space.scenery = createYardScenery({
    masterArtwork: {
      src: master.src,
      alt: master.alt,
    },
    extras: [...decals.sceneryLayers, ...props.sceneryLayers],
  });
  space.walkObstacles = [...decals.walkObstacles, ...props.walkObstacles];
  space.interactiveObjects = [...props.interactiveObjects];

  // Spawn under the porch roof so layout moves keep the child under cover.
  const roofDef = RALEIGH_PROP_DEFS.find((d) => d.id === 'porch-roof');
  if (roofDef && roofDef.enabled !== false) {
    const spawn = standWorldFromDef(
      withResolvedLayout(roofDef, mode),
      worldAspect,
    );
    space.start = spawn;
    space.arrivalSpawns = {
      ...(space.arrivalSpawns ?? {}),
      'places-map': spawn,
    };
  }
}

function placeSpanishForkPack(mode: BackyardPresentationMode) {
  const worldAspect = spanishForkAspectFor(mode);
  const scaleCtx = { sceneId: 'spanish-fork' as const, worldAspect };
  const overrides = getLayoutOverrides();
  const props = placeObjects(
    SPANISH_FORK_PROP_DEFS.map((d) =>
      withResolvedLayout(
        applyObjectLayoutOverride(d, 'spanish-fork', mode, overrides),
        mode,
        scaleCtx,
      ),
    ),
    { worldAspect },
  );
  const decals = placeGroundDecals(
    SPANISH_FORK_GROUND_DECAL_DEFS.map((d) =>
      withResolvedLayout(
        applyObjectLayoutOverride(d, 'spanish-fork', mode, overrides),
        mode,
        scaleCtx,
      ),
    ),
    { worldAspect },
  );
  return { props, decals, worldAspect };
}

/**
 * Rebuild Spanish Fork backyard master art + placed scenery/objects.
 */
export function applySpanishForkPresentation(
  space: PresenceSpace,
  mode: BackyardPresentationMode,
): void {
  if (space.id !== 'spanish-fork') return;
  const master = spanishForkMasterFor(mode);
  const { props, decals, worldAspect } = placeSpanishForkPack(mode);
  space.worldAspectRatio = worldAspect;
  space.scenery = createYardScenery({
    masterArtwork: {
      src: master.src,
      alt: master.alt,
    },
    extras: [...decals.sceneryLayers, ...props.sceneryLayers],
  });
  space.walkObstacles = [...decals.walkObstacles, ...props.walkObstacles];
  space.interactiveObjects = [...props.interactiveObjects];

  // Spawn under the porch so Grandma starts on the covered near edge.
  const roofDef = SPANISH_FORK_PROP_DEFS.find((d) => d.id === 'porch-roof');
  if (roofDef && roofDef.enabled !== false) {
    const spawn = standWorldFromDef(
      withResolvedLayout(roofDef, mode),
      worldAspect,
    );
    space.start = spawn;
    space.arrivalSpawns = {
      ...(space.arrivalSpawns ?? {}),
      'places-map': spawn,
    };
  }
}

/**
 * Blank place yard — same aspect / framing as Hubert, no master art yet.
 * Drop in desktop/mobile masters later the same way as Hubert.
 */
function blankPlaceSpace(
  id: string,
  displayName: string,
  description: string,
  yardScale = HUBERT_SCALE,
): PresenceSpace {
  const aspect = backyardAspectFor('desktop');
  return {
    id,
    displayName,
    description,
    scenery: createYardScenery({}),
    placeholderVisual: 'yard',
    worldWidth: 1.08,
    worldHeight: 1.08,
    worldAspectRatio: aspect,
    walkBounds: {
      xMin: 0.02,
      xMax: 0.98,
      yMin: 0.15,
      yMax: 0.97,
    },
    walkSpeed: 0.38,
    start: { x: 0.5, y: 0.84 },
    arrivalSpawns: {
      'places-map': { x: 0.5, y: 0.84 },
    },
    camera: storybookCamera({
      framingBiasX: 0.025,
      framingBiasY: 0.045,
      lookAhead: 0.028,
      panBounds: { xMin: 0.42, xMax: 0.58, yMin: 0.48, yMax: 0.78 },
      ...cameraDepthFromYardScale(yardScale),
    }),
    utilityTravel: [BACK_TO_MAP],
    interactiveObjects: [],
  };
}

const hubertWalkBounds = boundsFromPolygon(BACKYARD_WALK_POLYGON);

const hubertSpace: PresenceSpace = {
  id: 'hubert',
  displayName: 'Hubert, NC',
  description:
    'Grandpa’s backyard in Hubert. Walk the grass, visit the hammock, explore the fence line.',
  scenery: createYardScenery({}),
  placeholderVisual: 'yard',
  worldWidth: 1.08,
  worldHeight: 1.08,
  worldAspectRatio: backyardAspectFor('desktop'),
  walkBounds: hubertWalkBounds,
  walkPolygon: BACKYARD_WALK_POLYGON,
  walkObstacles: [],
  walkSpeed: 0.38,
  start: { x: 0.5, y: 0.84 },
  arrivalSpawns: {
    'places-map': { x: 0.5, y: 0.84 },
  },
  camera: storybookCamera({
    framingBiasX: 0.025,
    framingBiasY: 0.045,
    lookAhead: 0.028,
    panBounds: { xMin: 0.42, xMax: 0.58, yMin: 0.48, yMax: 0.78 },
    ...cameraDepthFromYardScale(HUBERT_SCALE),
  }),
  utilityTravel: [BACK_TO_MAP],
  interactiveObjects: [],
};

// Seed Hubert backyard. Prefer the editor-selected profile when ?edit=1 so
// Vite HMR after Save does not wipe tablet/mobile edits back to desktop.
applyBackyardPresentation(hubertSpace, resolveSeedLayoutProfile());

const raleighSpace = blankPlaceSpace(
  'raleigh',
  'Holly Springs, NC',
  'A backyard in Holly Springs. Walk the grass and explore.',
  RALEIGH_SCALE,
);
applyRaleighPresentation(raleighSpace, resolveSeedLayoutProfile());

function resolveSeedLayoutProfile(): 'desktop' | 'tablet' | 'mobile' {
  if (typeof sessionStorage === 'undefined') return 'desktop';
  try {
    if (
      typeof window !== 'undefined' &&
      !new URLSearchParams(window.location.search).has('edit')
    ) {
      return 'desktop';
    }
    const raw = sessionStorage.getItem('porchlight-editor-layout-profile');
    if (raw === 'desktop' || raw === 'tablet' || raw === 'mobile') return raw;
  } catch {
    /* ignore */
  }
  return 'desktop';
}

const spanishForkSpace = blankPlaceSpace(
  'spanish-fork',
  'Spanish Fork, UT',
  'A backyard in Spanish Fork. Walk the grass and explore.',
  SPANISH_FORK_SCALE,
);
applySpanishForkPresentation(spanishForkSpace, resolveSeedLayoutProfile());

// Grass bowl walkable area (gravel left / berms back+right stay out of bounds).
const SPANISH_FORK_WALK_POLYGON: readonly SpawnPoint[] = [
  { x: 0.26, y: 0.4 },
  { x: 0.7, y: 0.4 },
  { x: 0.82, y: 0.52 },
  { x: 0.88, y: 0.7 },
  { x: 0.86, y: 0.96 },
  { x: 0.26, y: 0.96 },
  { x: 0.22, y: 0.72 },
  { x: 0.22, y: 0.52 },
];
spanishForkSpace.walkPolygon = SPANISH_FORK_WALK_POLYGON;
spanishForkSpace.walkBounds = boundsFromPolygon(SPANISH_FORK_WALK_POLYGON);

/** Re-seed yards after layout overrides load (or owner save). */
export function reapplyAllYardPresentations(
  mode: BackyardPresentationMode = 'desktop',
): void {
  applyBackyardPresentation(hubertSpace, mode);
  applyRaleighPresentation(raleighSpace, mode);
  applySpanishForkPresentation(spanishForkSpace, mode);
}

/**
 * Hubert House — place-picker map into Hubert / Raleigh / Spanish Fork yards.
 * Internal world id stays `jacksonville-house` for storage compatibility.
 */
export const jacksonvilleHouseWorld = {
  id: 'jacksonville-house',
  name: 'Hubert House',
  startId: 'places-map',
  locations: {
    'places-map': {
      id: 'places-map',
      displayName: 'Where should we go?',
      defaultViewId: 'places-map',
      kind: 'travel-hub',
    },
    hubert: {
      id: 'hubert',
      displayName: 'Hubert, NC',
      defaultViewId: 'hubert',
      kind: 'presence',
    },
    raleigh: {
      id: 'raleigh',
      displayName: 'Holly Springs, NC',
      defaultViewId: 'raleigh',
      kind: 'presence',
    },
    'spanish-fork': {
      id: 'spanish-fork',
      displayName: 'Spanish Fork, UT',
      defaultViewId: 'spanish-fork',
      kind: 'presence',
    },
  },
  spaces: {
    hubert: hubertSpace,
    raleigh: raleighSpace,
    'spanish-fork': spanishForkSpace,
  },
  hubs: {
    'places-map': {
      id: 'places-map',
      displayName: 'Where should we go?',
      locationId: 'places-map',
      description: 'Choose a place to visit.',
      placeholderVisual: 'sky-map',
      placeholderLabel: 'Where should we go?',
      utilityTravel: [
        {
          id: 'to-hubert',
          label: 'Hubert, North Carolina',
          interactionType: 'travel',
          targetLocationId: 'hubert',
        },
        {
          id: 'to-raleigh',
          label: 'Holly Springs, North Carolina',
          interactionType: 'travel',
          targetLocationId: 'raleigh',
        },
        {
          id: 'to-spanish-fork',
          label: 'Spanish Fork, Utah',
          interactionType: 'travel',
          targetLocationId: 'spanish-fork',
        },
      ],
    },
  },
} satisfies WorldDefinition;
